import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, Calendar, Flame, Target, Phone, RefreshCw, ArrowRight, MessageCircle,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";

import AdminLayout from "@/components/admin/AdminLayout";
import { dashboard, rescoreAll, SITIO_BADGE, TEMP_BADGE, STATUS_OPTIONS } from "@/lib/admin-api";
import { Button } from "@/components/ui/button";

const CHANNEL_COLORS = { google_ads: "#E86A1F", meta_ads: "#4267B2", whatsapp: "#25D366", organico: "#10B981", directo: "#6B7280" };
const CHANNEL_LABELS = { google_ads: "Google Ads", meta_ads: "Meta Ads", whatsapp: "WhatsApp", organico: "Orgánico", directo: "Directo" };

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [rescoring, setRescoring] = useState(false);
    const user = JSON.parse(localStorage.getItem("admin_user") || "{}");

    useEffect(() => { dashboard().then(setData).catch(() => toast.error("No se pudo cargar dashboard")); }, []);

    const onRescore = async () => {
        setRescoring(true);
        try {
            const r = await rescoreAll();
            toast.success(`Recalculado: ${r.updated_count} leads actualizados`);
            dashboard().then(setData);
        } catch { toast.error("Falló rescore"); }
        finally { setRescoring(false); }
    };

    if (!data) return <AdminLayout title="Dashboard"><div className="text-eclipse-muted">Cargando...</div></AdminLayout>;

    const { kpis, widget_hoy, chart_14d, pie_canales, ultimos_5_leads } = data;
    const topHot = widget_hoy.hot_sin_contactar[0];

    return (
        <AdminLayout title="Dashboard">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl text-eclipse-primary" data-testid="dashboard-greeting">Hola, {user.nombre?.split(" ")[0] || "Admin"}</h2>
                    <p className="text-sm text-eclipse-muted">Bienvenido al panel Eclipse Cubiertas</p>
                </div>
                <Button onClick={onRescore} disabled={rescoring} data-testid="dashboard-rescore-btn" className="btn-eclipse-outline-dark text-xs px-4 py-2 rounded-sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${rescoring ? "animate-spin" : ""}`} />
                    Recalcular scores
                </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Kpi Icon={Users} label="Total leads" value={kpis.total_leads} testId="kpi-total" />
                <Kpi Icon={Calendar} label="Leads hoy" value={kpis.leads_hoy} testId="kpi-hoy" />
                <Kpi Icon={Flame} label="Leads HOT" value={kpis.leads_hot} accent testId="kpi-hot" />
                <Kpi Icon={Target} label="En cierre" value={kpis.leads_en_cierre} testId="kpi-cierre" />
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-sm p-6 mb-6" data-testid="widget-hoy">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="font-display font-black text-xl text-eclipse-primary inline-flex items-center gap-2"><Flame className="w-5 h-5 text-eclipse-cta" /> Hoy</h3>
                    {topHot && (
                        <a href={`tel:${topHot.telefono}`} data-testid="widget-call-next-hot" className="btn-eclipse-cta px-5 py-2.5 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5" /> Llamar siguiente HOT
                        </a>
                    )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-center">
                    <MiniStat label="Seguimientos" value={widget_hoy.seguimientos_hoy} />
                    <MiniStat label="Vencidos" value={widget_hoy.vencidos} alert={widget_hoy.vencidos > 0} />
                    <MiniStat label="HOT sin contactar" value={widget_hoy.hot_sin_contactar.length} alert={widget_hoy.hot_sin_contactar.length > 0} />
                    <MiniStat label="En cierre" value={widget_hoy.en_cierre} />
                </div>
                <div className="bg-white rounded-sm overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-neutral-200 text-xs font-display font-bold uppercase tracking-wider text-eclipse-primary">Top 5 HOT sin contactar</div>
                    {widget_hoy.hot_sin_contactar.length === 0 ? (
                        <p className="p-4 text-sm text-eclipse-muted">Sin leads hot pendientes 🎉</p>
                    ) : (
                        <table className="w-full text-sm">
                            <tbody>
                                {widget_hoy.hot_sin_contactar.map((l) => (
                                    <tr key={l.id} className="border-b border-neutral-100 last:border-0 hover:bg-orange-50/50">
                                        <td className="px-4 py-2.5 font-medium text-eclipse-primary">{l.nombre}</td>
                                        <td className="px-2 py-2.5"><Badge {...SITIO_BADGE[l.sitio]} /></td>
                                        <td className="px-2 py-2.5 text-eclipse-muted text-xs">{l.telefono}</td>
                                        <td className="px-2 py-2.5 text-eclipse-cta font-display font-black">{l.lead_score}</td>
                                        <td className="px-2 py-2.5 text-eclipse-muted text-xs hidden sm:table-cell">{l.created_at && formatDistanceToNow(parseISO(l.created_at), { addSuffix: true, locale: es })}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            <Link to={`/admin/leads/${l.id}`} className="text-eclipse-cta hover:underline inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider" data-testid={`widget-hot-lead-${l.id}`}>
                                                Ver <ArrowRight className="w-3 h-3" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-sm p-5">
                    <h3 className="font-display font-bold text-sm uppercase tracking-wider text-eclipse-primary mb-4">Leads por sitio (últimos 14 días)</h3>
                    <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={chart_14d.map((d) => ({ ...d, day: format(parseISO(d.day), "d MMM", { locale: es }) }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="eclipse" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="flexshade" stroke="#E86A1F" strokeWidth={2} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="velarias" stroke="#4F46E5" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white border border-neutral-200 rounded-sm p-5">
                    <h3 className="font-display font-bold text-sm uppercase tracking-wider text-eclipse-primary mb-4">Canales</h3>
                    <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                            <Pie data={pie_canales} dataKey="count" nameKey="channel" cx="50%" cy="50%" outerRadius={70} label={(e) => CHANNEL_LABELS[e.channel] || e.channel}>
                                {pie_canales.map((c, i) => <Cell key={i} fill={CHANNEL_COLORS[c.channel] || "#999"} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
                <h3 className="font-display font-bold text-sm uppercase tracking-wider text-eclipse-primary px-5 py-4 border-b border-neutral-200">Últimos 5 leads</h3>
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-eclipse-muted">
                        <tr>
                            <th className="px-4 py-2 text-left">Fecha</th>
                            <th className="px-4 py-2 text-left">Nombre</th>
                            <th className="px-4 py-2 text-left">Sitio</th>
                            <th className="px-4 py-2 text-left">Temp</th>
                            <th className="px-4 py-2 text-left">Estatus</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ultimos_5_leads.map((l) => {
                            const st = STATUS_OPTIONS.find((s) => s.value === l.estatus);
                            return (
                                <tr key={l.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                                    <td className="px-4 py-3 text-xs text-eclipse-muted">{l.created_at && format(parseISO(l.created_at), "d MMM HH:mm", { locale: es })}</td>
                                    <td className="px-4 py-3 font-medium">{l.nombre}</td>
                                    <td className="px-4 py-3"><Badge {...SITIO_BADGE[l.sitio]} /></td>
                                    <td className="px-4 py-3"><Badge {...TEMP_BADGE[l.lead_temp]} bordered /></td>
                                    <td className="px-4 py-3"><Badge label={st?.label || l.estatus} color={st?.color} bordered /></td>
                                    <td className="px-4 py-3 text-right">
                                        <Link to={`/admin/leads/${l.id}`} className="text-eclipse-cta hover:underline text-xs font-bold uppercase">Ver →</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </AdminLayout>
    );
}

function Kpi({ Icon, label, value, accent, testId }) {
    return (
        <div className={`bg-white border ${accent ? "border-eclipse-cta" : "border-neutral-200"} rounded-sm p-5`} data-testid={testId}>
            <div className="flex items-center justify-between mb-3">
                <Icon className={`w-5 h-5 ${accent ? "text-eclipse-cta" : "text-eclipse-muted"}`} />
                {accent && <span className="text-[9px] font-bold bg-eclipse-cta text-white px-1.5 py-0.5 rounded">HOT</span>}
            </div>
            <div className="font-display font-black text-3xl text-eclipse-primary leading-none">{value}</div>
            <p className="text-xs text-eclipse-muted mt-1">{label}</p>
        </div>
    );
}

function MiniStat({ label, value, alert }) {
    return (
        <div className="bg-white rounded-sm p-3">
            <div className={`font-display font-black text-2xl leading-none ${alert ? "text-eclipse-cta" : "text-eclipse-primary"}`}>{value}</div>
            <p className="text-[11px] text-eclipse-muted mt-1">{label}</p>
        </div>
    );
}

export function Badge({ label, color = "bg-neutral-100 text-neutral-700", bordered }) {
    return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded ${color} ${bordered ? "border" : ""}`}>{label}</span>;
}
